// Global Instructions Rule Applied!
//
// Shared helpers for working with a resolved distribution-list recipient
// set (the bare-array output of view__distribution_list, wrapped by the
// backend into {data:[...]}).
//
// Extracted from
// frontend/src/app_modules/distribution_lists/components/SendCommunicationPage.tsx
// when Email Phase 3 grew a second consumer (the email-campaign compose
// form's Recipients tab). CLAUDE.md frontend rule: "If you have customised
// a component, try to make it a local standard and reuse it." Same logic,
// one place to maintain (RFC-5322 addressee formatting, lowercased-email
// dedupe, account/contact tree builder).
//
// Pure functions — no React, no Redux, no fetch. Callers reuse these with
// their own state (exclusions, expansion, etc.) layered on top.

import type { DistributionListRecipient } from '../../state_manager/data_models/distribution_list_recipients_model.ts'

// ── Identity + presence ─────────────────────────────────────────────────────

/** Stable composite key for a (account, contact) pair. */
export const pairKey = (r: DistributionListRecipient): string =>
  `${r.accounts__id}::${r.contacts__id}`

/** True iff the recipient has a non-empty email address. The resolve RPC
 *  returns rows with NULL emails (so the screen can show them as excluded);
 *  callers use this to gate sendability. */
export const hasEmail = (r: DistributionListRecipient): boolean =>
  !!(r.contacts__email && r.contacts__email.trim())

/** Display name fallback chain: "First Last" → email → "(no name)". */
export const contactName = (r: DistributionListRecipient): string => {
  const full = `${r.contacts__first_name ?? ''} ${r.contacts__last_name ?? ''}`.trim()
  return full || r.contacts__email || '(no name)'
}

// ── RFC-5322 formatting + dedupe ────────────────────────────────────────────

/** Format one recipient as an RFC-5322 address for an email client:
 *  `First Last <email>`. The display name is double-quoted iff it contains
 *  a comma / semicolon / quote / angle bracket (so the separator is
 *  unambiguous in Outlook and Gmail); a name-less contact degrades to the
 *  bare address. Callers should only pass recipients with an email (use
 *  hasEmail() to gate). */
export const formatAddr = (r: DistributionListRecipient): string => {
  const email = (r.contacts__email || '').trim()
  const name = `${r.contacts__first_name ?? ''} ${r.contacts__last_name ?? ''}`
    .replace(/\s+/g, ' ')
    .trim()
  if (!name) return email
  const safe = /[",;<>]/.test(name) ? `"${name.replace(/"/g, '\\"')}"` : name
  return `${safe} <${email}>`
}

/** De-duplicate recipients by lowercased email, preserving first-seen order.
 *  Rows with no email are dropped. */
export const dedupeByEmail = (
  rows: DistributionListRecipient[]
): DistributionListRecipient[] => {
  const seen = new Set<string>()
  const out: DistributionListRecipient[] = []
  for (const r of rows) {
    const k = (r.contacts__email || '').trim().toLowerCase()
    if (!k || seen.has(k)) continue
    seen.add(k)
    out.push(r)
  }
  return out
}

// ── Account / contact tree ──────────────────────────────────────────────────

export interface AccountNode {
  key: string
  isAccount: true
  accountId: string
  accountName: string
  /** Every recipient row under this account (in resolve order). */
  contacts: DistributionListRecipient[]
  /** AntD Table child rows. */
  children: ContactNode[]
}

export interface ContactNode {
  key: string
  isAccount: false
  accountId: string
  recipient: DistributionListRecipient
}

export type ResolveGridRow = AccountNode | ContactNode

/** Group the flat (account, contact) resolve rows into a collapsible
 *  accounts → contacts tree, sorted by account name. */
export const buildAccountTree = (
  recipients: DistributionListRecipient[] | null | undefined
): AccountNode[] => {
  if (!recipients || recipients.length === 0) return []
  const byAccount = new Map<string, AccountNode>()
  for (const r of recipients) {
    let node = byAccount.get(r.accounts__id)
    if (!node) {
      node = {
        key: `acct:${r.accounts__id}`,
        isAccount: true,
        accountId: r.accounts__id,
        accountName: r.accounts__name || '(unnamed account)',
        contacts: [],
        children: []
      }
      byAccount.set(r.accounts__id, node)
    }
    node.contacts.push(r)
    node.children.push({
      key: `pair:${pairKey(r)}`,
      isAccount: false,
      accountId: r.accounts__id,
      recipient: r
    })
  }
  return Array.from(byAccount.values()).sort((a, b) =>
    a.accountName.localeCompare(b.accountName)
  )
}
